import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { createSubscription } from '../lib/realtimeHelpers';
import { useInitiativeStore } from './initiativeStore';
import { useCharacterStore } from './characterStore';
import { useMobStore } from './mobStore';
import { useEncounterStore } from './encounterStore';
import { useCampaignStore } from './campaignStore';
import { isCurrentUserGM } from '../hooks/useIsGM';

/**
 * Combat Store — Thin proxy to encounterStore
 *
 * Maintains backward compatibility while encounterStore is the new
 * single source of truth. All mutations delegate to encounterStore.
 * Reads still work from local state (kept in sync by encounterStore
 * pushing to initiativeStore + this store's realtime subscription).
 *
 * @deprecated Prefer useEncounterStore for new code.
 */

export interface InitiativeEntry {
  characterId: string;
  initiative: number;
  dexMod?: number; // For tiebreakers
}

interface DbCombatState {
  id: string;
  scene_id: string;
  round_number: number;
  current_turn_index: number;
  is_active: boolean;
  initiative_order: InitiativeEntry[];
}

interface CombatState {
  // State
  inCombat: boolean;
  initiativeOrder: InitiativeEntry[];
  currentTurnIndex: number;
  round: number;

  // Supabase sync
  currentSceneId: string | null;
  combatStateId: string | null;
  realtimeCleanup: (() => void) | null;
  isLoading: boolean;

  // Actions
  startCombat: () => void;
  endCombat: () => void;
  /** Explicit setter — call from wherever the active scene resolves */
  setCurrentSceneId: (sceneId: string | null) => void;
  /**
   * Resync current combat state to DB without resetting — used to recover a
   * paused battle that was never written to machina_combat_state. Creates the
   * encounter, adds participants from initiativeOrder, preserves current round.
   */
  resyncToDb: (round?: number) => Promise<void>;

  // Initiative
  setInitiative: (characterId: string, initiative: number, dexMod?: number) => void;
  clearInitiative: (characterId: string) => void;
  clearAllInitiative: () => void;

  // Turn management
  nextTurn: () => void;
  prevTurn: () => void;
  setCurrentTurn: (index: number) => void;

  // Queries
  getInitiative: (characterId: string) => number | null;
  getTurnOrder: () => string[]; // Returns characterIds in initiative order

  // Supabase sync
  loadFromSupabase: (sceneId: string) => Promise<void>;
  syncToSupabase: () => Promise<void>;
  subscribeToRealtime: (sceneId: string) => void;
  unsubscribeFromRealtime: () => void;
}

export const useCombatStore = create<CombatState>((set, get) => ({
  inCombat: false,
  initiativeOrder: [],
  currentTurnIndex: 0,
  round: 1,
  currentSceneId: null,
  combatStateId: null,

  setCurrentSceneId: (sceneId) => {
    const prev = get().currentSceneId;
    if (prev !== sceneId) {
      console.log(`[combatStore] currentSceneId: ${prev ?? 'null'} → ${sceneId ?? 'null'}`);
      set({ currentSceneId: sceneId });
    }
  },

  resyncToDb: async (roundOverride) => {
    const encStore = useEncounterStore.getState();
    const sceneId = get().currentSceneId;
    const campaignId = useCampaignStore.getState().currentCampaignId;

    if (!sceneId || !campaignId) {
      console.warn('[combatStore] resyncToDb: no sceneId or campaignId — cannot resync');
      return;
    }

    // If no encounter exists, create one and mark active (without the beginCombat reset)
    if (!encStore.activeEncounter) {
      encStore.startEncounter(sceneId, campaignId);
    }

    // Scrub orphan character participants — UUIDs that no longer resolve in
    // this campaign (cross-campaign bleed left in the JSONB blob).
    const charStore = useCharacterStore.getState();
    const mobStore = useMobStore.getState();
    const enc0 = useEncounterStore.getState().activeEncounter;
    if (enc0 && charStore.currentCampaignId === campaignId && charStore.characters.size > 0) {
      const cleaned = new Map(enc0.participants);
      let scrubbed = 0;
      for (const [pid, p] of cleaned) {
        if (p.type === 'character' && !charStore.characters.has(pid)) {
          console.warn(`[combatStore] Resync scrubbing orphan participant: ${p.name} (${pid})`);
          cleaned.delete(pid);
          scrubbed++;
        }
      }
      if (scrubbed > 0) {
        // Clear currentTurnId if it pointed at a dropped participant
        const currentTurnId = enc0.currentTurnId && cleaned.has(enc0.currentTurnId)
          ? enc0.currentTurnId
          : null;
        useEncounterStore.setState({
          activeEncounter: { ...enc0, participants: cleaned, currentTurnId },
        });
      }
    }

    // Add every combatant from initiativeOrder as a participant
    const order = get().initiativeOrder;
    for (const entry of order) {
      const char = charStore.getCharacter(entry.characterId);
      const mob = char ? null : mobStore.mobs.get(entry.characterId);
      if (!char && !mob) continue;
      const existing = useEncounterStore.getState().activeEncounter?.participants.get(entry.characterId);
      if (existing) {
        useEncounterStore.getState().setParticipantInitiative(entry.characterId, entry.initiative);
      } else {
        const name = char?.name || mob?.displayName || mob?.name || 'Unknown';
        const type = char ? 'character' as const : 'mob' as const;
        useEncounterStore.getState().addParticipant(
          entry.characterId, type, name, entry.initiative, entry.dexMod ?? 0
        );
      }
    }

    // Manually flip to active status + preserve current round (avoid beginCombat's reset)
    const enc = useEncounterStore.getState().activeEncounter;
    if (enc) {
      const preservedRound = roundOverride ?? Math.max(1, get().round);
      useEncounterStore.setState({
        activeEncounter: { ...enc, status: 'active', round: preservedRound },
      });
      await useEncounterStore.getState().syncToSupabase();
      set({ inCombat: true });
      console.log(`[combatStore] resyncToDb complete — round ${preservedRound}, ${order.length} combatants`);
    }
  },
  realtimeCleanup: null,
  isLoading: false,

  startCombat: () => {
    const encStore = useEncounterStore.getState();
    const sceneId = get().currentSceneId;
    const campaignId = useCampaignStore.getState().currentCampaignId;
    const charStore = useCharacterStore.getState();
    const mobStore = useMobStore.getState();

    // Self-heal: create encounter if one doesn't exist yet
    if (!encStore.activeEncounter && sceneId && campaignId) {
      encStore.startEncounter(sceneId, campaignId);
    }

    // Seed encounter from existing initiative order (rolls the player may
    // already have made), then auto-include ALL alive mobs on the board that
    // aren't in init yet. No more 'Add Monsters' management step — if a mob
    // is on the board and combat starts, it's in the fight.
    if (useEncounterStore.getState().activeEncounter) {
      const encParticipants = useEncounterStore.getState().activeEncounter!.participants;

      // 1) Existing initiativeOrder entries
      for (const entry of get().initiativeOrder) {
        if (encParticipants.has(entry.characterId)) continue;
        const char = charStore.getCharacter(entry.characterId);
        const mob = char ? null : mobStore.mobs.get(entry.characterId);
        if (!char && !mob) continue;
        const name = char?.name || mob?.displayName || mob?.name || 'Unknown';
        const type = char ? 'character' as const : 'mob' as const;
        useEncounterStore.getState().addParticipant(
          entry.characterId, type, name, entry.initiative, entry.dexMod ?? 0
        );
      }

      // 2) Alive mobs that don't already have an init roll — auto-roll now
      const refreshed = useEncounterStore.getState().activeEncounter!.participants;
      for (const mob of mobStore.mobs.values()) {
        if (mob.dead) continue;
        if (refreshed.has(mob.id)) continue;
        const dexMod = Math.floor((mob.dexterity - 10) / 2);
        const initiative = Math.floor(Math.random() * 20) + 1 + dexMod;
        useEncounterStore.getState().addParticipant(
          mob.id, 'mob', mob.displayName || mob.name, initiative, dexMod
        );
        // Keep combatStore's initiativeOrder in sync for UI reads
        set((state) => ({
          initiativeOrder: [
            ...state.initiativeOrder.filter((e) => e.characterId !== mob.id),
            { characterId: mob.id, initiative, dexMod },
          ].sort((a, b) => b.initiative - a.initiative || (b.dexMod || 0) - (a.dexMod || 0)),
        }));
      }
    }

    const withEncounter = !!useEncounterStore.getState().activeEncounter;
    if (withEncounter) {
      useEncounterStore.getState().beginCombat();
    }
    set({ inCombat: true, round: 1, currentTurnIndex: 0 });
    if (!withEncounter) get().syncToSupabase();
  },

  endCombat: () => {
    const encStore = useEncounterStore.getState();
    const hadEncounter = !!encStore.activeEncounter;
    if (hadEncounter) {
      encStore.endEncounter(); // encounterStore handles DB sync + clears initiativeStore
    } else {
      // Clear initiative panel when no encounter was active
      useInitiativeStore.getState().clearAllEntries();
    }
    set({
      inCombat: false,
      initiativeOrder: [],
      currentTurnIndex: 0,
      round: 1
    });
    if (!hadEncounter) get().syncToSupabase();
  },

  setInitiative: (characterId, initiative, dexMod = 0) => {
    // Bridge to encounterStore — auto-create encounter if needed
    const encStore = useEncounterStore.getState();
    if (!encStore.activeEncounter) {
      const sceneId = get().currentSceneId;
      const campaignId = useCampaignStore.getState().currentCampaignId;
      if (sceneId && campaignId) {
        encStore.startEncounter(sceneId, campaignId);
      }
    }
    const delegated = !!encStore.activeEncounter;
    if (delegated) {
      const existing = encStore.activeEncounter!.participants.get(characterId);
      if (existing) {
        encStore.setParticipantInitiative(characterId, initiative);
      } else {
        // Resolve name and type
        const charStore = useCharacterStore.getState();
        const mobStore = useMobStore.getState();
        const char = charStore.getCharacter(characterId);
        const mob = char ? null : mobStore.mobs.get(characterId);
        const name = char?.name || mob?.displayName || mob?.name || 'Unknown';
        const type = char ? 'character' as const : 'mob' as const;
        encStore.addParticipant(characterId, type, name, initiative, dexMod);
      }
    }

    set((state) => {
      const filtered = state.initiativeOrder.filter(e => e.characterId !== characterId);
      const newOrder = [...filtered, { characterId, initiative, dexMod }];
      newOrder.sort((a, b) => {
        if (b.initiative !== a.initiative) return b.initiative - a.initiative;
        return (b.dexMod || 0) - (a.dexMod || 0);
      });
      return {
        initiativeOrder: newOrder,
        inCombat: state.inCombat || newOrder.length > 0,
      };
    });

    // When not delegated to encounterStore, push to initiativeStore directly
    // so mob entries appear in the initiative panel UI
    if (!delegated) {
      const initStore = useInitiativeStore.getState();
      const charStore = useCharacterStore.getState();
      const mobStore = useMobStore.getState();
      const char = charStore.getCharacter(characterId);
      const mob = char ? null : mobStore.mobs.get(characterId);
      const name = char?.name || mob?.displayName || mob?.name || 'Unknown';
      const type = char ? 'character' as const : 'mob' as const;

      // Use char-${id} prefix for characters to match InitiativePanel + encounterStore convention
      const panelId = char ? `char-${characterId}` : characterId;
      const existing = initStore.entries.find(e => e.id === panelId);
      if (existing) {
        initStore.setInitiative(panelId, initiative);
      } else {
        initStore.addEntry({
          id: panelId,
          name,
          initiative,
          dexMod,
          type,
          characterId: char ? characterId : undefined,
          conditions: [],
        });
      }

      get().syncToSupabase();
    }
  },

  clearInitiative: (characterId) => {
    const encStore = useEncounterStore.getState();
    const delegated = !!encStore.activeEncounter;
    if (delegated) {
      encStore.removeParticipant(characterId);
    }
    set((state) => ({
      initiativeOrder: state.initiativeOrder.filter(e => e.characterId !== characterId),
    }));
    if (!delegated) get().syncToSupabase();
  },

  clearAllInitiative: () => {
    set({ initiativeOrder: [] });
    useInitiativeStore.getState().clearAllEntries();
    get().syncToSupabase();
  },

  nextTurn: () => {
    const encStore = useEncounterStore.getState();
    const delegated = !!encStore.activeEncounter;
    if (delegated) {
      encStore.nextTurn(); // encounterStore handles DB sync
    }
    set((state) => {
      if (state.initiativeOrder.length === 0) return state;
      const nextIndex = (state.currentTurnIndex + 1) % state.initiativeOrder.length;
      const newRound = nextIndex === 0 ? state.round + 1 : state.round;
      return { currentTurnIndex: nextIndex, round: newRound };
    });
    if (!delegated) get().syncToSupabase();
  },

  prevTurn: () => {
    const encStore = useEncounterStore.getState();
    const delegated = !!encStore.activeEncounter;
    if (delegated) {
      encStore.prevTurn(); // encounterStore handles DB sync
    }
    set((state) => {
      if (state.initiativeOrder.length === 0) return state;
      const prevIndex = state.currentTurnIndex === 0
        ? state.initiativeOrder.length - 1
        : state.currentTurnIndex - 1;
      const newRound = state.currentTurnIndex === 0 && state.round > 1
        ? state.round - 1
        : state.round;
      return { currentTurnIndex: prevIndex, round: newRound };
    });
    if (!delegated) get().syncToSupabase();
  },

  setCurrentTurn: (index) => {
    set({ currentTurnIndex: index });
    get().syncToSupabase();
  },

  getInitiative: (characterId) => {
    const entry = get().initiativeOrder.find(e => e.characterId === characterId);
    return entry?.initiative ?? null;
  },

  getTurnOrder: () => get().initiativeOrder.map(e => e.characterId),

  // ==========================================================================
  // SUPABASE SYNC
  // ==========================================================================

  loadFromSupabase: async (sceneId) => {
    set({ isLoading: true, currentSceneId: sceneId });
    try {
      const { data, error } = await supabase
        .from('machina_combat_state')
        .select('*')
        .eq('scene_id', sceneId)
        .single();

      if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows

      if (data) {
        const dbState = data as DbCombatState;

        // Skip inactive encounters — prevents ghost mobs from stale records
        if (!dbState.is_active) {
          console.log('[CombatStore] Skipping inactive encounter (stale combat_state)');
          set({ inCombat: false, initiativeOrder: [], currentTurnIndex: 0, round: 1, combatStateId: null, isLoading: false });
          return;
        }

        // READ-ONLY. Same legacy-bridge bug as subscribeToRealtime above —
        // resolving each entry against local stores and pruning unresolved
        // ones writes destructively back through initiativeStore →
        // encounterStore → syncToSupabase. encounterStore.loadFromSupabase
        // is the canonical hydrator for participants + initiativeStore mirror.
        // combatStore.loadFromSupabase only updates its own legacy fields.
        set({
          inCombat: dbState.is_active,
          initiativeOrder: dbState.initiative_order || [],
          currentTurnIndex: dbState.current_turn_index,
          round: dbState.round_number,
          combatStateId: dbState.id,
          isLoading: false,
        });
      } else {
        set({
          inCombat: false,
          initiativeOrder: [],
          currentTurnIndex: 0,
          round: 1,
          combatStateId: null,
          isLoading: false,
        });
      }
    } catch (err) {
      console.error('Failed to load combat state:', err);
      set({ isLoading: false });
    }
  },

  syncToSupabase: async () => {
    // PUB/SUB GATE: combat_state is GM-owned. See encounterStore.syncToSupabase
    // for the full rationale.
    if (!isCurrentUserGM()) {
      return;
    }

    // SINGLE WRITER: encounterStore owns every write to machina_combat_state.
    // combatStore used to INSERT/UPDATE the same UNIQUE(scene_id) row in
    // parallel, which raced encounterStore on first write — one INSERT won, the
    // other took a 23505/409 and was silently dropped (observed live). We no
    // longer touch the table here. Instead we mirror combatStore's legacy
    // fields into the canonical encounter and let encounterStore persist (it
    // upserts on scene_id + retries + surfaces failures).
    const { currentSceneId, inCombat, initiativeOrder, currentTurnIndex, round } = get();
    if (!currentSceneId) return;

    const encStore = useEncounterStore.getState();

    // Ensure an encounter exists to carry the state. These syncToSupabase calls
    // only fire on the non-delegated paths (no activeEncounter), so seed one
    // from the legacy initiative state before delegating.
    if (!encStore.activeEncounter) {
      const campaignId = useCampaignStore.getState().currentCampaignId;
      if (!campaignId) {
        console.warn('[combatStore] syncToSupabase: no campaignId — cannot seed encounter for delegated write');
        return;
      }
      encStore.startEncounter(currentSceneId, campaignId);

      const charStore = useCharacterStore.getState();
      const mobStore = useMobStore.getState();
      for (const entry of initiativeOrder) {
        const char = charStore.getCharacter(entry.characterId);
        const mob = char ? null : mobStore.mobs.get(entry.characterId);
        const name = char?.name || mob?.displayName || mob?.name || 'Unknown';
        const type = char ? ('character' as const) : ('mob' as const);
        useEncounterStore.getState().addParticipant(
          entry.characterId, type, name, entry.initiative, entry.dexMod ?? 0
        );
      }
    }

    // Reflect combatStore's turn/round/active state onto the encounter, then
    // hand off the single write to encounterStore.
    const enc = useEncounterStore.getState().activeEncounter;
    if (!enc) return;

    const sorted = useEncounterStore.getState().getParticipantsSorted();
    const turnIdx = Math.min(Math.max(0, currentTurnIndex), Math.max(0, sorted.length - 1));
    const currentTurnId = sorted.length > 0 ? sorted[turnIdx].id : null;

    useEncounterStore.setState({
      activeEncounter: {
        ...enc,
        status: inCombat ? 'active' : enc.status === 'completed' ? 'completed' : 'setup',
        round,
        currentTurnId,
      },
    });

    await useEncounterStore.getState().syncToSupabase();
  },

  subscribeToRealtime: (sceneId) => {
    // READ-ONLY MIRROR. combatStore is a legacy view over machina_combat_state
    // that predates encounterStore. encounterStore is now the canonical owner
    // of participants and the initiativeStore mirror — having combatStore
    // ALSO bridge to initiativeStore caused two destructive bugs:
    //
    //   1. The bridge resolved each `initiative_order` entry against the local
    //      characterStore + mobStore, then REMOVED any initiativeStore entry
    //      that didn't resolve. On initial page load mobStore hadn't hydrated
    //      yet, so every mob got pruned via removeEntry → encounterStore.
    //      removeParticipant → syncToSupabase → realtime echo to all clients.
    //
    //   2. With N clients connected, each one's bridge ran independently with
    //      its own characterStore visibility (players see fewer chars than the
    //      GM), and each one wrote its scrubbed view back. The result was an
    //      N-way feedback loop that saturated the GM's network connection
    //      pool with ERR_INSUFFICIENT_RESOURCES.
    //
    // We keep the realtime sub itself only to update the legacy combatStore
    // read fields (inCombat, round, currentTurnIndex) for components that
    // still read them. We do NOT touch initiativeStore from here — that mirror
    // belongs to encounterStore via its own subscribeToRealtime + zustand
    // subscribe-and-derive pipeline.
    get().unsubscribeFromRealtime();

    const { cleanup } = createSubscription({
      channelName: `combat:${sceneId}`,
      table: 'machina_combat_state',
      filter: `scene_id=eq.${sceneId}`,
      event: '*',
      onPayload: (payload) => {
        if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
          const dbState = payload.new as unknown as DbCombatState;
          set({
            inCombat: dbState.is_active,
            initiativeOrder: dbState.initiative_order || [],
            currentTurnIndex: dbState.current_turn_index,
            round: dbState.round_number,
            combatStateId: dbState.id,
          });
        } else if (payload.eventType === 'DELETE') {
          set({
            inCombat: false,
            initiativeOrder: [],
            currentTurnIndex: 0,
            round: 1,
            combatStateId: null,
          });
        }
      },
    });

    set({ realtimeCleanup: cleanup, currentSceneId: sceneId });
  },

  unsubscribeFromRealtime: () => {
    const { realtimeCleanup } = get();
    if (realtimeCleanup) {
      realtimeCleanup();
      set({ realtimeCleanup: null });
    }
  },
}));
